// src/container/.../sod/authorized-filers/AuthorizedFilerDetails.js
import React, { useEffect, useState, useCallback, startTransition } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Form,
  Input,
  Tag,
  Alert,
  Spin,
} from 'antd';
// import { apiPath } from '../../../../../constants/defaultValues';
import moment from 'moment';
import fileDownload from 'js-file-download';
import axios from '../../../../helpers/Axios';

const { TextArea } = Input;

const authHeaders = {
  headers: {
    'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY_AUTHENTICATION,
  },
};

const AuthorizedFilerDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { filer_id, company_id } = state;

  const [filerDetails, setFilerDetails] = useState([]);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [loadingFiler, setLoadingFiler] = useState(true);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [downloadingBOR, setDownloadingBOR] = useState(false);
  const [alert, setAlert] = useState(null);
  // const [showSpinner, setShowSpinner] = useState(false);

  // const isInitialLoading = loadingFiler && loadingCompany && !alert;

  // if (isInitialLoading) {
  //   return (
  //     <div style={{ textAlign: 'center', padding: 40 }}>
  //       <Spin size="large" />
  //     </div>
  //   );
  // }

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
    window.scrollTo(0, 0);
  }, []);

  // useEffect(() => {
  //   const t = setTimeout(() => setShowSpinner(true), 150);
  //   return () => clearTimeout(t);
  // }, []);

  useEffect(() => {
    if (!filer_id) {
      showAlert('error', 'No filer selected.');
      setLoadingFiler(false);
      setLoadingCompany(false);
      return;
    }

    // Authorized filer details
    axios
      .get(`https://api.fortisph.space/gateway/authentication/filers`,
        { authHeaders,
          params: { filer_id },
        })
      .then((res) => {
        console.log('filers data:', res.data);
        setFilerDetails(res.data || []);
      })
      .catch(() => {
        showAlert('error', 'Failed to load filer details.');
      })
      .finally(() => setLoadingFiler(false));

    // Company details if company_id is present
    if (company_id) {
      axios
        .get(`/api/get_company/`, { params: { id: company_id } })
        .then((res) => {
          setCompanyDetails(res.data || []);
        })
        .catch(() => {
          showAlert('error', 'Failed to load company details.');
        })
        .finally(() => setLoadingCompany(false));
    } else {
      setCompanyDetails([]);
      setLoadingCompany(false);
    }
  }, [filer_id, company_id, showAlert]);

  const handleBack = () => {
    startTransition(() => {
      navigate('..');
    })
  };

  const downloadBORFile = async () => {
    if (!company_id) return;
    try {
      setDownloadingBOR(true);
      const res = await axios.get(
        `https://api.fortisph.space/gateway/authentication/filers/bor/`,
        {
          authHeaders,
          params: { company_id },
          responseType: 'blob',
        }
      );
      const filename = `BOR_${moment(new Date()).format('DDMMYYYY_HHmmss')}.pdf`;
      fileDownload(res.data, filename);
    } catch (error) {
      showAlert('error', error.message || 'Failed to download file.');
    } finally {
      setDownloadingBOR(false);
    }
  };

  const DisabledInput = ({ label, value }) => (
    <Form.Item label={label}>
      <Input value={value || ''} disabled />
    </Form.Item>
  );

  return (
    <>
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          showIcon
          closable
          onClose={() => setAlert(null)}
          className="mb-3"
        />
      )}

      <Row justify="center">
        <Col xs={24} lg={20} xl={18}>
          {loadingFiler ? (
            <div className="flex justify-center py-6">
              <Spin size="large" />
            </div>
          ) : !loadingFiler && !filerDetails.length ? (
            <Card className="mb-4">
              <p>No authorized filer data found.</p>
            </Card>
          ) : (
            filerDetails.map((filer) => (
              <Card className="mt-4 mb-4" key={filer.id} bodyStyle={{ padding: '16px 24px' }}>
                <Row justify="space-between" align="middle" gutter={16}>
                  <Col>
                    <h3><strong>Authorized Filer</strong></h3>
                  </Col>
                  <Col>
                    <Row gutter={16} align="middle">
                      <Col>
                        <strong>Enrollment Status: </strong>
                        <Tag color={filer.enrolled ? 'green' : 'red'}>
                          {filer.enrolled ? 'Enrolled' : 'Not Enrolled'}
                        </Tag>
                      </Col>
                      <Col>
                        <strong>Status: </strong>
                        <Tag
                          color={filer.status_span_class || 'default'}
                          style={{
                            borderRadius: 5,
                            padding: '2px 6px',
                            color: 'white',
                            fontSize: 11,
                          }}
                        >
                          {filer.status_name}
                        </Tag>
                      </Col>
                      <Col>
                        <Button size="small" onClick={handleBack}>
                          ← Back to List
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <hr />

                <Form layout="vertical">
                  <Row gutter={16}>
                    <Col xs={24} md={6}>
                      <DisabledInput label="Title" value={filer.filer_title} />
                    </Col>
                    <Col xs={24} md={18}>
                      <DisabledInput label="Last Name" value={filer.filer_last_name} />
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <DisabledInput label="First Name" value={filer.filer_first_name} />
                    </Col>
                    <Col xs={24} md={8}>
                      <DisabledInput label="Middle Name" value={filer.filer_middle_name} />
                    </Col>
                    <Col xs={24} md={8}>
                      <DisabledInput label="Suffix" value={filer.filer_suffix} />
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <DisabledInput
                        label="Birth Date"
                        value={
                          filer.user__birth_date
                            ? moment(filer.user__birth_date).format('MM/DD/YYYY')
                            : ''
                        }
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <DisabledInput label="TIN" value={filer.user__tin} />
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <DisabledInput label="Username" value={filer.username} />
                    </Col>
                    <Col xs={24} md={8}>
                      <DisabledInput label="Email Address" value={filer.filer_email} />
                    </Col>
                    <Col xs={24} md={8}>
                      <DisabledInput
                        label="Landline / Mobile No."
                        value={filer.filer_mobile_no}
                      />
                    </Col>
                  </Row>

                  <DisabledInput
                    label="Contact Address"
                    value={filer.contact_address}
                  />

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <DisabledInput
                        label="Position"
                        value={filer.filer_position}
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <DisabledInput
                        label="Filer Type"
                        value={filer.filer_filer_type}
                      />
                    </Col>
                  </Row>
                </Form>

                <hr />

                <Row justify="space-between" align="middle" gutter={16}>
                  <Col xs={24} md={18}>
                    <h5>
                      Board Resolution (Majority Signed) or Secretary Certificate
                      (Notarized)
                    </h5>
                  </Col>
                  <Col xs={24} md={6} className="text-right">
                    {!filer.board_resolution ? null : (
                      <Button
                        size="small"
                        loading={downloadingBOR}
                        onClick={downloadBORFile}
                      >
                        Download BOR
                      </Button>
                    )}
                  </Col>
                </Row>

                {/* If you want inline PDF view similar to PdfViewer, you can embed iframe here
                  pointing to `${apiPath}/api/filer_attachment_pdf_view/?company_id=${company_id}&filer_id=${filer.id}&status_id=${filer.status}` */}
              </Card>
            ))
          )}

          {loadingCompany ? (
            <div className="flex justify-center py-6">
              <Spin size="large" />
            </div>
          ) : !loadingCompany && !companyDetails.length ? null : (
            companyDetails.map((company) => (
              <React.Fragment key={company.id}>
                <Card className="mb-4"
                  bodyStyle={{ padding: '16px 24px' }}>
                  <Row justify="space-between" gutter={16}>
                    <Col xs={24} md={16}>
                      <h3><strong>Company Information</strong></h3>
                    </Col>
                    <Col xs={24} md={8}>
                      <Row gutter={16}>
                        <Col xs={12}>
                          <strong>Exceptional: </strong>
                          <Tag color={company.exceptional ? 'red' : 'green'}>
                            {String(company.exceptional)}
                          </Tag>
                        </Col>
                        <Col xs={12}>
                          <strong>Status: </strong>
                          <Tag
                            color={company.status__span_class || 'default'}
                            style={{
                              borderRadius: 5,
                              padding: '2px 6px',
                              color: 'white',
                              fontSize: 11,
                            }}
                          >
                            {company.status__name}
                          </Tag>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <hr />

                  <Form layout="vertical">
                    <Row gutter={16}>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="SEC Registration No."
                          value={company.sec_no}
                        />
                      </Col>
                      <Col xs={24} md={16}>
                        <DisabledInput
                          label="Company Name"
                          value={company.name}
                        />
                      </Col>
                    </Row>

                    {/* Principal Office Address */}
                    <h3 className="mt-4 mb-2"><strong>Principal Office Address</strong></h3>
                    <Row gutter={16}>
                      <Col xs={24} md={6}>
                        <DisabledInput
                          label="Floor"
                          value={company.office_address__floor}
                        />
                      </Col>
                      <Col xs={24} md={18}>
                        <DisabledInput
                          label="Building"
                          value={company.office_address__building}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={6}>
                        <DisabledInput
                          label="Number"
                          value={company.office_address__number}
                        />
                      </Col>
                      <Col xs={24} md={18}>
                        <DisabledInput
                          label="Street"
                          value={company.office_address__street}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Barangay"
                          value={company.office_address__barangay__name}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="City/Town"
                          value={company.office_address__city__name}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Province"
                          value={company.office_address__province__name}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={16}>
                        <DisabledInput
                          label="Region"
                          value={company.office_address__region__name}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Zip Code"
                          value={company.office_address__zip_code}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <DisabledInput
                          label="Geographical Code"
                          value={company.office_address__city__psgc_code}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <DisabledInput
                          label="Location"
                          value={company.office_address__city__name}
                        />
                      </Col>
                    </Row>

                    {/* Business Address */}
                    <h3 className="mt-4 mb-2"><strong>Business Address</strong></h3>
                    <Row gutter={16}>
                      <Col xs={24} md={6}>
                        <DisabledInput
                          label="Floor"
                          value={company.business_address__floor}
                        />
                      </Col>
                      <Col xs={24} md={18}>
                        <DisabledInput
                          label="Building"
                          value={company.business_address__building}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={6}>
                        <DisabledInput
                          label="Number"
                          value={company.business_address__number}
                        />
                      </Col>
                      <Col xs={24} md={18}>
                        <DisabledInput
                          label="Street"
                          value={company.business_address__street}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Barangay"
                          value={company.business_address__barangay__name}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="City/Town"
                          value={company.business_address__city__name}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Province"
                          value={company.business_address__province__name}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={16}>
                        <DisabledInput
                          label="Region"
                          value={company.business_address__region__name}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Zip Code"
                          value={company.business_address__zip_code}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <DisabledInput
                          label="Geographical Code"
                          value={company.business_address__city__psgc_code}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <DisabledInput
                          label="Location"
                          value={company.business_address__city__name}
                        />
                      </Col>
                    </Row>
                  </Form>
                </Card>

                <Card className="mb-4" bodyStyle={{ padding: '16px 24px' }}>
                  <h3><strong>Contact Person</strong></h3>
                  <hr />
                  <Form layout="vertical">
                    <Row gutter={16}>
                      <Col xs={24} md={6}>
                        <DisabledInput
                          label="Title"
                          value={company.contact_person_title}
                        />
                      </Col>
                      <Col xs={24} md={18}>
                        <DisabledInput
                          label="Last Name"
                          value={company.contact_person_last_name}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="First Name"
                          value={company.contact_person_first_name}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Middle Name"
                          value={company.contact_person_middle_name}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Suffix"
                          value={company.contact_person_suffix}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Username"
                          value={company.user__username}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Official Email Address"
                          value={company.contact_person_email}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DisabledInput
                          label="Alternate Email Address"
                          value={company.contact_person_alternate_email}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <DisabledInput
                          label="Official Landline / Mobile No."
                          value={company.contact_person_mobile_no}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <DisabledInput
                          label="Alternate Landline / Mobile No."
                          value={company.contact_person_alternate_contact_no}
                        />
                      </Col>
                    </Row>
                  </Form>
                </Card>
              </React.Fragment>
            ))
          )}
        </Col>
      </Row>
    </>
  );
};

export default AuthorizedFilerDetails;
